/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BulkUpdateRestService,
  ComponentRestService,
  HousePlanRestService,
  ItemRestService,
} from '@gh/builder-shared-data';
import { InitViewService } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { BulkUpdateItemComponentListItem } from './bulk-update-item-component-list-item.model';
import {
  BulkUpdateComponentAdd,
  BulkUpdateComponentDelete,
  BulkUpdateComponentReplace,
  BulkUpdateHousePlanAdd,
  BulkUpdateHousePlanDelete,
  BulkUpdateHousePlanReplace,
  ComponentBulkUpdateListItem,
  ItemBulkUpdateListItem,
} from './bulk-update.model';
import { HousePlanBulkUpdate, HousePlanBulkUpdateAutocomplete } from './house-plan-bulk-update.model';

@Injectable()
export class BulkUpdateBusinessService implements InitViewService {

  constructor(private hpRestService: HousePlanRestService,
              private bulkUpdateRestService: BulkUpdateRestService,
              private componentRestService: ComponentRestService,
              private itemRestService: ItemRestService) {
  }

  initView(): any {
    return {
      initData: this.bulkUpdateRestService.getReplaceInitData(),
    };
  }

  loadHousePlans(query: FilteredDataQuery<string>): Observable<HousePlanBulkUpdateAutocomplete[]> {
    return this.hpRestService.findAllAsBulkUpdateAutocompleteDataSource({
      searchString: query.filter,
      hideZeroCost: false,
      autocomplete: true,
    }).select(query).pipe(map$(getSlicedData));
  }

  loadComponents(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.componentRestService.findAllAsBulkUpdateAutocompleteDataSource(query.filter).select(query).pipe(
      map$(getSlicedData));
  }

  loadHousePlansGridDataSource(hpId?: ID): SlicedDataSource<HousePlanBulkUpdate> {
    return this.hpRestService.findAllAsBulkUpdateDataSource({
      hideZeroCost: true,
      autocomplete: false,
      excludeIds: hpId ? [hpId] : void 0,
    });
  }

  loadComponentGridDataSource(componentId?: ID): SlicedDataSource<ComponentBulkUpdateListItem> {
    return this.componentRestService.findAllAsBulkUpdateDataSource({
      excludeIds: componentId ? [componentId] : void 0,
    });
  }

  loadItemsComponentsByHp(housePlan: HousePlanBulkUpdateAutocomplete): Observable<BulkUpdateItemComponentListItem[]> {
    return this.bulkUpdateRestService.loadItemsComponentsByHp(housePlan.id);
  }

  addHpSave(hpAdd: BulkUpdateHousePlanAdd): Observable<any> {
    return this.bulkUpdateRestService.addHpSave(hpAdd);
  }

  addComponentSave(componentAdd: BulkUpdateComponentAdd): Observable<any> {
    return this.bulkUpdateRestService.addComponentSave(componentAdd);
  }

  replaceHpSave(replaceHp: BulkUpdateHousePlanReplace): Observable<any> {
    return this.bulkUpdateRestService.replaceHpSave(replaceHp);
  }

  replaceComponentSave(replaceComponent: BulkUpdateComponentReplace): Observable<any> {
    return this.bulkUpdateRestService.replaceComponentSave(replaceComponent);
  }

  deleteHpSave(deleteHp: BulkUpdateHousePlanDelete): Observable<any> {
    return this.bulkUpdateRestService.deleteHpSave(deleteHp);
  }

  deleteComponentSave(deleteComponent: BulkUpdateComponentDelete): Observable<any> {
    return this.bulkUpdateRestService.deleteComponentSave(deleteComponent);
  }

  loadItemsByComponent(id: ID): Observable<ItemBulkUpdateListItem[]> {
    return this.itemRestService.getItemListByComponent(id);
  }
}
